'use client';
// ──────────────────────────────────────────────────────
// AiProductShowcase — AI-powered product carousel
//
// Premium 3D-inspired product showcase for kiosk homepage.
// Fetches image-backed products and displays them in a
// cinematic auto-rotating carousel.
//
// Features:
//   - Fetches real products from backend (image-only filter)
//   - 3D perspective card transitions
//   - Auto-rotation with smooth spring physics
//   - Product image + name + category display
//   - Gradient progress indicator
//   - Responsive, RTL-safe
// ──────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ShowcaseProduct {
    id: string;
    nameAr: string | null;
    brand: string | null;
    category: string | null;
    imageUrl: string;
}

const ROTATE_MS = 4500;

// 3D card transition variants
const cardVariants = {
    enter: (dir: number) => ({
        rotateY: dir > 0 ? 45 : -45,
        opacity: 0,
        scale: 0.85,
        x: dir > 0 ? 80 : -80,
    }),
    center: {
        rotateY: 0,
        opacity: 1,
        scale: 1,
        x: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 180,
            damping: 24,
            mass: 0.8,
        },
    },
    exit: (dir: number) => ({
        rotateY: dir > 0 ? -45 : 45,
        opacity: 0,
        scale: 0.85,
        x: dir > 0 ? -80 : 80,
        transition: { duration: 0.35, ease: 'easeIn' as const },
    }),
};

export function AiProductShowcase() {
    const [products, setProducts] = useState<ShowcaseProduct[]>([]);
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(1);
    const [loading, setLoading] = useState(true);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Fetch image-backed products
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`/api/products/showcase`);
                if (!res.ok) throw new Error('fetch failed');
                const data = await res.json();
                const items: ShowcaseProduct[] = (data.products || data.data || data || [])
                    .filter((p: Record<string, unknown>) => p.imageUrl && typeof p.imageUrl === 'string')
                    .map((p: Record<string, unknown>) => ({
                        id: p.id as string,
                        nameAr: (p.nameAr || p.name || '') as string,
                        brand: (p.brand || '') as string,
                        category: (p.category || '') as string,
                        imageUrl: p.imageUrl as string,
                    }));
                if (!cancelled && items.length > 0) setProducts(items);
            } catch {
                // Silently fail — showcase is decorative, not critical
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Auto-rotate
    const advance = useCallback(() => {
        setDirection(1);
        setIndex((prev) => (prev + 1) % (products.length || 1));
    }, [products.length]);

    useEffect(() => {
        if (products.length < 2) return;
        timerRef.current = setInterval(advance, ROTATE_MS);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [products.length, advance]);

    if (loading) {
        return (
            <div className="w-full max-w-md mx-auto">
                <div className="glass-card p-6 flex flex-col items-center gap-3" style={{ height: 240 }}>
                    <div className="shimmer w-24 h-24 rounded-2xl" />
                    <div className="shimmer w-3/5 h-4 rounded" />
                    <div className="shimmer w-2/5 h-3 rounded" />
                </div>
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div className="w-full max-w-md mx-auto">
                <div className="glass-card p-6 flex flex-col items-center justify-center gap-3 text-center" style={{ height: 220 }}>
                    <span className="text-4xl">✨</span>
                    <h3 className="text-xl font-semibold text-[var(--color-foreground)]">ترشيحات ذكية</h3>
                    <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                        الذكاء الاصطناعي يرشّح لك منتجات مناسبة لنوع بشرتك
                    </p>
                </div>
            </div>
        );
    }

    const product = products[index];

    return (
        <div className="w-full max-w-md mx-auto">
            {/* Section header */}
            <div className="flex items-center justify-center gap-2 mb-4">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
                    style={{
                        width: 18, height: 18,
                        border: '2px solid #0ea5a5',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                    }}
                />
                <p style={{ fontSize: 13, fontWeight: 600, color: '#0ea5a5', letterSpacing: '-0.01em' }}>
                    منتجات مختارة بالذكاء الاصطناعي
                </p>
            </div>

            {/* 3D Carousel container */}
            <div
                style={{
                    position: 'relative',
                    height: 260,
                    perspective: '800px',
                    overflow: 'hidden',
                }}
            >
                <AnimatePresence mode="popLayout" custom={direction}>
                    <motion.div
                        key={product.id + '-' + index}
                        custom={direction}
                        variants={cardVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: 20,
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8fffe 100%)',
                            border: '1px solid rgba(14,165,165,0.12)',
                            boxShadow: '0 8px 32px rgba(14,165,165,0.08), 0 2px 8px rgba(0,0,0,0.04)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 12,
                            padding: 20,
                            transformStyle: 'preserve-3d',
                        }}
                    >
                        {/* Product image */}
                        <div
                            style={{
                                width: 110,
                                height: 110,
                                borderRadius: 16,
                                overflow: 'hidden',
                                background: '#f1f5f9',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                                flexShrink: 0,
                            }}
                        >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={product.imageUrl}
                                alt={product.nameAr || 'Product'}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                                loading="lazy"
                            />
                        </div>

                        {/* Product info */}
                        <div style={{ textAlign: 'center', maxWidth: '90%' }}>
                            <p style={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: '#1a1a2e',
                                lineHeight: 1.4,
                                marginBottom: 4,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            }}>
                                {product.nameAr || 'منتج'}
                            </p>
                            {product.category && (
                                <p style={{
                                    fontSize: 12,
                                    color: '#0ea5a5',
                                    fontWeight: 500,
                                }}>
                                    {product.category}
                                </p>
                            )}
                        </div>

                        {/* AI badge */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            borderRadius: 20,
                            background: 'rgba(14,165,165,0.06)',
                            border: '1px solid rgba(14,165,165,0.12)',
                        }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0ea5a5" strokeWidth="2">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                            </svg>
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#0ea5a5' }}>AI Pick</span>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Progress bar */}
            <div style={{
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
                marginTop: 16,
            }}>
                {products.slice(0, Math.min(products.length, 12)).map((_, i) => (
                    <div
                        key={i}
                        style={{
                            width: i === index % Math.min(products.length, 12) ? 20 : 6,
                            height: 4,
                            borderRadius: 2,
                            background: i === index % Math.min(products.length, 12)
                                ? '#0ea5a5'
                                : 'rgba(14,165,165,0.15)',
                            transition: 'all 0.35s ease',
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
